import api from './api';

export interface ShadowErrorSignature {
  stage: string | null;
  exc: string | null;
  n: number;
  lastSeen: string;
  sample: string;
}

export interface SaverulesHealth {
  lastOkAt: string | null;
  lastErrorAt: string | null;
  minsSinceOk: number | null;
  saves24h: number;
  /** No OK heartbeat for 6h — the feed is DARK and every other number here is frozen. */
  stale: boolean;
  /**
   * The shadow threw on at least one save in the window. The feed is still live and the
   * rest of the page is current; only the erroring saves lack parity coverage. Separate
   * from `stale` so a recurring error can't pin the liveness light red forever.
   *
   * Optional: undefined on API builds predating the stale/errored split — the banner
   * treats that as not-erroring, same convention as the optional panels below.
   */
  errored?: boolean;
  errors: {
    rows: number;
    saves: number;
    bySignature: ShadowErrorSignature[];
  };
}

/**
 * A value-diff already traced to a defect on the V1 side — our value is the correct
 * one. Labelled rather than suppressed, so the row stays visible but reads as a closed
 * finding. Source of truth: backend/app/services/saverules/known_v1_defects.py.
 */
export interface V1Defect {
  label: string;
  note: string;
}

export interface ValueDiffDetail {
  field: string | null;
  rule: string | null;
  python: string | null;
  v1: string | null;
  n: number;
  v1Defect: V1Defect | null;
}

export interface SaverulesParity {
  window: string;
  saves: number;
  cleanSaves: number;
  /**
   * Clean saves when extras (Python wrote MORE than v1) are not held against the save —
   * only value_diffs count as dirty. Optional: undefined on older API builds.
   */
  cleanSavesNoExtra?: number;
  cleanOf: number;
  valueDiffs: number;
  /** Per-kind divergence counts. Optional: undefined on older API builds. */
  byKind?: {
    missing: number;
    effect_missing: number;
    extra: number;
    effect_extra: number;
    value_diff: number;
  };
  /** Of `valueDiffs`, those traced to a known v1 defect (see V1Defect). */
  valueDiffsV1Defect: number;
  fieldExtra: number;
  effectExtra: number;
  matchedWrites: number;
  missingWrites: number;
  srDriverMissing: number;
  baseSaveMissing: number;
  valueDiffDetail: ValueDiffDetail[];
}

export interface SaverulesGap {
  name: string;
  n: number;
  owner: 'driver' | 'basesave' | 'parked' | 'portable' | string;
  // Which save path produced the gap: 'trace' = gwitrace milestone path (recolored so
  // the trace path can be watched on its own), 'interactive' = human saves.
  source?: 'trace' | 'interactive' | string;
}

export interface TimingSegment {
  segment: string;
  n: number;
  meanMs: number;
  maxMs: number;
  native: boolean;
}

export interface ReconcilerArea {
  ops: number;
  benign: number;
  real: number;
}

export interface SaverulesReconciler {
  window: string;
  saves: number;
  match: number;
  skip: number;
  opSaves: number;
  ops: number;
  benign: number;
  real: number;
  realSaves: number;
  cleanSaves: number;
  cleanOf: number;
  byArea: Record<string, ReconcilerArea>;
  divergenceTypes: Record<string, Record<string, number>>;
  cutover: { pros: number; v2: number; inflight: number; dollars: number };
  /** REAL divergences per area split by cause: v2-quote (transient, resolves as the v2
   *  migration completes) vs other. Absent on older API builds. */
  causes?: Record<string, { v2_quote: number; other: number }>;
}

/** One column the native writer sent that the row came back holding differently —
 *  silent coercion, truncation, a concurrent writer, or a `*current` mirror
 *  double-write that matched no row. See write_verify.py. */
export interface WriteVerifyColumn {
  table: string;
  field: string;
  n: number;
  pros: number;
  lastSeen: string;
  /** "<sent> -> <stored>" for the most recent occurrence. */
  sample: string;
  /** True for orderscurrent / legscurrent: the read-copy double-write missed, which is
   *  a different fault (and a different fix) from the DB rewriting a value.
   *  Absent on API builds predating mirror coverage. */
  mirror?: boolean;
}

export interface SaverulesWriteVerify {
  window: string;
  rows: number;
  saves: number;
  pros: number;
  lastSeen: string | null;
  /** Saves the audit actually inspected, from its per-save heartbeat. `rows: 0` only
   *  means "everything landed" when this is non-zero — a clean save writes no
   *  divergence rows at all, so without it zero is indistinguishable from "the native
   *  writer never ran here". Absent on API builds predating the heartbeat. */
  auditedSaves?: number;
  /** Total rows re-read across those saves — the depth behind `auditedSaves`. */
  checkedRows?: number;
  lastAuditAt?: string | null;
  byColumn: WriteVerifyColumn[];
}

/** What `cms_table_sync` had to repair between the `*all` tables and their
 *  `*current` read copies. Independent of which writer ran, so it covers native,
 *  cmsapi, V1 Perl and bulk jobs alike. */
export interface MirrorDriftDay {
  day: string;
  runs: number;
  updated: number;
  inserted: number;
  worstUpdated: number;
}

export interface SaverulesMirrorDrift {
  window: string;
  /** False when sync_job_history isn't readable here — the panel says so rather than
   *  rendering zeros as health. */
  available: boolean;
  runs: number;
  updated: number;
  /** Legs the job INSERTED. Never routine: it repairs a drifted column by copying it,
   *  but legnum is one of its JOIN keys, so a leg whose number the mirror missed gets
   *  duplicated rather than renumbered. Non-zero = a write-path bug. */
  inserted: number;
  worstUpdated: number;
  worstInserted: number;
  runsWithDrift: number;
  failedRuns: number;
  lastRun: string | null;
  medianDaily: number;
  legIdentityDrift: boolean;
  /** Day whose repairs ran >=10x the window median, or null. */
  spikeDay: string | null;
  byDay: MirrorDriftDay[];
}

/** One class of shadow row that is NOT cutover risk, held out of every parity
 *  number and shown on its own so the exclusion stays auditable. `kind`:
 *  - `v1_defect`    — v1 misbehaves and we deliberately don't copy it
 *  - `cutover_only` — v1 skips it only because the write went through cmsapi
 *  - `out_of_scope` — a v1 write no saverule owns (user / save-envelope)
 *  - `artifact`     — both engines did the same thing; the comparison can't see it */
export interface ExcludedSignature {
  key: string;
  label: string;
  kind: 'v1_defect' | 'cutover_only' | 'out_of_scope' | 'artifact' | string;
  why: string;
  impact: string;
  n: number;
  saves: number;
  pros: number;
  lastSeen: string | null;
}

export interface SaverulesExcluded {
  window: string;
  rows: number;
  bySignature: ExcludedSignature[];
}

export interface SaverulesDashboardSummary {
  asOf: string | null;
  window: string;
  days: number;
  portedRules: number;
  health: SaverulesHealth;
  parity: SaverulesParity;
  gaps: SaverulesGap[];
  /** Absent on API builds older than the known-non-divergence exclusion. */
  excluded?: SaverulesExcluded;
  reconciler: SaverulesReconciler;
  /** Absent on API builds older than the native write-verify audit. */
  writeVerify?: SaverulesWriteVerify;
  /** Absent on API builds older than the *current mirror-drift panel. */
  mirror?: SaverulesMirrorDrift;
  timing: { window: string; saves: number; segments: TimingSegment[] };
}

// ---- drill-down --------------------------------------------------------

export type DrilldownSection =
  | 'gap' | 'valuediff' | 'extra' | 'missing' | 'error' | 'save' | 'reconciler'
  | 'write_verify' | 'excluded';

/** A raw shadow_log row (saverules sections). */
export interface DrilldownRow {
  /** Set when this value_diff is a known v1-side defect; null otherwise. */
  v1Defect: V1Defect | null;
  id: number;
  pronumber: string | number | null;
  legNum: string | number | null;
  ruleName: string | null;
  diffKind: string | null;
  fieldName: string | null;
  pythonValue: string | null;
  v1Value: string | null;
  userId: string | null;
  requestId: string | null;
  createdAt: string | null;
}

export interface ReconcilerOpSide {
  qty: string | null;
  rate: string | null;
  type: string | null;
  total: string | null;
}

/** A reconciler op row with the invoice-line before/after parsed out. */
export interface ReconcilerDrillRow {
  id: number;
  pronumber: string | number | null;
  area: string;
  item: string | null;
  action: string;
  reason: string;
  optype: string;
  isReal: boolean;
  before: ReconcilerOpSide | null;
  after: ReconcilerOpSide | null;
  userId: string | null;
  requestId: string | null;
  createdAt: string | null;
}

export interface DrilldownResponse<T = DrilldownRow | ReconcilerDrillRow> {
  section: DrilldownSection;
  total: number;
  limit: number;
  offset: number;
  rows: T[];
}

export interface DrilldownParams {
  section: DrilldownSection;
  days: number;
  bucket?: string;
  area?: string;
  optype?: string;
  request_id?: string;
  pronumber?: string;
  user?: string;
  rule?: string;
  field?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface RollupRow {
  key: string;
  n: number;
  pros: number;
}

export interface TrendPoint {
  date: string;
  n: number;
}

/** A pro's CURRENT invoice line (live cms.invoice_lineitem context). */
export interface ReconcilerLine {
  item: string | null;
  region: string | null;
  qty: string | number | null;
  rate: string | number | null;
  rateType: string | null;
  total: string | number | null;
  description: string | null;
  lineNumber: number | null;
  accessorial: string | number | null;
}

const BASE = '/admin/saverules-dashboard';

const saverulesDashboardService = {
  async getSummary(days: number, signal?: AbortSignal): Promise<SaverulesDashboardSummary> {
    const res = await api.get<SaverulesDashboardSummary>(`${BASE}/summary`, {
      params: { days },
      signal,
    });
    return res.data;
  },

  async getDrilldown<T = DrilldownRow | ReconcilerDrillRow>(
    params: DrilldownParams,
    signal?: AbortSignal,
  ): Promise<DrilldownResponse<T>> {
    const res = await api.get<DrilldownResponse<T>>(`${BASE}/drilldown`, { params, signal });
    return res.data;
  },

  async getRollup(
    by: 'rule' | 'user',
    section: DrilldownSection,
    days: number,
    bucket = '',
    signal?: AbortSignal,
  ): Promise<RollupRow[]> {
    const res = await api.get<{ by: string; rows: RollupRow[] }>(`${BASE}/drilldown/rollup`, {
      params: { by, section, days, bucket },
      signal,
    });
    return res.data.rows;
  },

  async getTrend(
    section: DrilldownSection,
    days: number,
    bucket = '',
    signal?: AbortSignal,
  ): Promise<TrendPoint[]> {
    const res = await api.get<{ points: TrendPoint[] }>(`${BASE}/drilldown/trend`, {
      params: { section, days, bucket },
      signal,
    });
    return res.data.points;
  },

  async getReconcilerLines(pronumber: number | string, signal?: AbortSignal): Promise<ReconcilerLine[]> {
    const res = await api.get<{ pronumber: number; lines: ReconcilerLine[] }>(
      `${BASE}/reconciler-lines`,
      { params: { pronumber }, signal },
    );
    return res.data.lines;
  },
};

export default saverulesDashboardService;
